import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';

export interface PlaceholderPageProps {
  titleKey: string;
  messageKey?: string;
  /**
   * Optional breadcrumb override. When omitted, the page infers a breadcrumb
   * from the current URL:
   *   • `/admin/*`  → "관리자 대시보드 > <section> > <title>"
   *   • everything else → just the page title
   */
  breadcrumb?: Array<{ labelKey: string; to?: string }>;
}

/**
 * Map an `/admin/*` pathname to its enclosing sidebar section, so the
 * breadcrumb reads like the real menu hierarchy (e.g.
 * "관리자 대시보드 > 계정관리 > 유저 관리") instead of an action like "돌아가기".
 */
function inferAdminBreadcrumb(pathname: string, titleKey: string): Array<{ labelKey: string; to?: string }> | null {
  if (!pathname.startsWith('/admin')) return null;

  const accountManagement = ['/admin/users', '/admin/user-roles'];
  const activityManagement = ['/admin/campaigns', '/admin/campaign-managers'];
  const customerService = ['/admin/notices', '/admin/inquiries'];
  const otherSettings = ['/admin/settings'];

  let sectionKey: string | null = null;
  if (accountManagement.includes(pathname)) sectionKey = 'admin:sidebar.accountManagement';
  else if (activityManagement.includes(pathname)) sectionKey = 'admin:sidebar.activityManagement';
  else if (customerService.includes(pathname)) sectionKey = 'admin:sidebar.customerService';
  else if (otherSettings.includes(pathname)) sectionKey = 'admin:sidebar.otherSettings';

  const crumbs: Array<{ labelKey: string; to?: string }> = [
    { labelKey: 'common:profileDropdown.adminDashboard', to: '/admin' },
  ];
  if (sectionKey) crumbs.push({ labelKey: sectionKey });
  crumbs.push({ labelKey: titleKey });

  return crumbs;
}

/**
 * Placeholder page styled to match the Public Campaign View:
 * gray-50 background, breadcrumb header, and a white rounded info card.
 * The breadcrumb reflects the real menu hierarchy (inferred from the URL
 * for /admin/* routes) so it reads as proper navigation depth.
 */
const PlaceholderPage: React.FC<PlaceholderPageProps> = ({
  titleKey,
  messageKey = 'myProfile.placeholder.comingSoon',
  breadcrumb,
}) => {
  const { t } = useTranslation(['profile', 'common', 'admin']);
  const location = useLocation();
  const navigate = useNavigate();

  const title = t(titleKey);

  const crumbs =
    breadcrumb ?? inferAdminBreadcrumb(location.pathname, titleKey) ?? [{ labelKey: titleKey }];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb — mirrors PublicCampaignDetails */}
      <div className="max-w-5xl mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <React.Fragment key={`${c.labelKey}-${i}`}>
                {c.to && !isLast ? (
                  <button
                    type="button"
                    onClick={() => navigate(c.to!)}
                    className="hover:text-blue-600 transition-colors"
                  >
                    {t(c.labelKey)}
                  </button>
                ) : (
                  <span className={isLast ? 'text-gray-900 font-medium' : ''}>
                    {t(c.labelKey)}
                  </span>
                )}
                {!isLast && <span className="mx-1.5">&gt;</span>}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>

          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-blue-50 flex items-center justify-center">
                <ClockIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold text-gray-900">
                    {t('myProfile.placeholder.inProgressTitle', {
                      defaultValue: 'Feature in progress',
                    })}
                  </h2>
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                    {t('myProfile.placeholder.badge', { defaultValue: 'Coming Soon' })}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                  {t(messageKey)}
                </p>
              </div>
            </div>
          </div>

          <div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {t('myProfile.placeholder.goBack', { defaultValue: '돌아가기' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceholderPage;
